import React, { useState, useEffect } from "react";
import {
  collection,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  getDoc,
  setDoc,
} from "firebase/firestore";
import { db } from "../firebase";
import "./AdminDashboard.css";

function AdminDashboard() {
  const [events, setEvents] = useState([]);
  const [editingEvent, setEditingEvent] = useState(null);
  const [formData, setFormData] = useState({
    title: "",
    date: "",
    time: "",
    description: "",
    cost: "",
    duration: "",
    website: "",
    imgLink: "",
    highlighted: false,
  });
  const [loading, setLoading] = useState(false);
  const [siteSettings, setSiteSettings] = useState({
    showEvents: true, // Default to showing events
  });
  const [savingSettings, setSavingSettings] = useState(false);

  useEffect(() => {
    fetchEvents();
    fetchSiteSettings();
  }, []);

  const fetchSiteSettings = async () => {
    try {
      const settingsRef = doc(db, "siteSettings", "global");
      const settingsSnap = await getDoc(settingsRef);

      if (settingsSnap.exists()) {
        setSiteSettings(settingsSnap.data());
      } else {
        // Create default settings if they don't exist
        const defaultSettings = {
          showEvents: true,
          showClasses: true,
          updatedAt: new Date().toISOString(),
        };
        await setDoc(settingsRef, defaultSettings);
        setSiteSettings(defaultSettings);
      }
    } catch (err) {
      console.error("Error fetching site settings:", err);
    }
  };

  const toggleEventsVisibility = async () => {
    try {
      setSavingSettings(true);
      const newValue = !siteSettings.showEvents;

      const settingsRef = doc(db, "siteSettings", "global");
      await updateDoc(settingsRef, {
        showEvents: newValue,
        updatedAt: new Date().toISOString(),
      });

      setSiteSettings((prev) => ({
        ...prev,
        showEvents: newValue,
      }));

      // Show feedback
      alert(
        `Sección de eventos ${newValue ? "activada" : "desactivada"} correctamente.`,
      );
    } catch (err) {
      console.error("Error toggling events visibility:", err);
      alert(
        "Error al cambiar la visibilidad de eventos. Por favor intente nuevamente.",
      );
    } finally {
      setSavingSettings(false);
    }
  };

  const fetchEvents = async () => {
    setLoading(true);
    const querySnapshot = await getDocs(collection(db, "events"));
    const eventsList = [];
    querySnapshot.forEach((doc) => {
      eventsList.push({
        id: doc.id,
        ...doc.data(),
      });
    });
    setEvents(eventsList.sort((a, b) => new Date(a.date) - new Date(b.date)));
    setLoading(false);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === "checkbox" ? checked : value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.title || !formData.date || !formData.time) {
      alert("Por favor complete todos los campos obligatorios");
      return;
    }

    const eventData = {
      ...formData,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };

    // Clean up empty fields
    if (!eventData.cost) delete eventData.cost;
    if (!eventData.duration) delete eventData.duration;
    if (!eventData.description) delete eventData.description;
    if (!eventData.website) delete eventData.website;
    if (!eventData.imgLink) delete eventData.imgLink;

    try {
      if (editingEvent) {
        const eventRef = doc(db, "events", editingEvent.id);
        await updateDoc(eventRef, {
          ...eventData,
          updatedAt: new Date().toISOString(),
        });
        setEditingEvent(null);
      } else {
        await addDoc(collection(db, "events"), eventData);
      }

      setFormData({
        title: "",
        date: "",
        time: "",
        description: "",
        cost: "",
        duration: "",
        website: "",
        imgLink: "",
        highlighted: false,
      });
      fetchEvents();
    } catch (error) {
      console.error("Error saving event:", error);
      alert("Error al guardar el evento. Por favor intente nuevamente.");
    }
  };

  const handleEdit = (event) => {
    setEditingEvent(event);
    setFormData({
      title: event.title || "",
      date: event.date || "",
      time: event.time || "",
      description: event.description || "",
      cost: event.cost || "",
      duration: event.duration || "",
      website: event.website || "",
      imgLink: event.imgLink || "",
      highlighted: event.highlighted || false,
    });
  };

  const handleDelete = async (eventId) => {
    if (window.confirm("¿Está seguro de que desea eliminar este evento?")) {
      try {
        await deleteDoc(doc(db, "events", eventId));
        fetchEvents();
      } catch (error) {
        console.error("Error deleting event:", error);
        alert("Error al eliminar el evento. Por favor intente nuevamente.");
      }
    }
  };

  const handleCancel = () => {
    setEditingEvent(null);
    setFormData({
      title: "",
      date: "",
      time: "",
      description: "",
      cost: "",
      duration: "",
      website: "",
      imgLink: "",
      highlighted: false,
    });
  };

  const toggleHighlight = async (eventId, currentHighlighted) => {
    try {
      const eventRef = doc(db, "events", eventId);
      await updateDoc(eventRef, {
        highlighted: !currentHighlighted,
        updatedAt: new Date().toISOString(),
      });

      setEvents(
        events.map((event) =>
          event.id === eventId
            ? { ...event, highlighted: !currentHighlighted }
            : event,
        ),
      );
    } catch (error) {
      console.error("Error toggling highlight:", error);
      alert(
        "Error al cambiar el estado destacado. Por favor intente nuevamente.",
      );
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("es-AR", {
      weekday: "short",
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  return (
    <div className="admin-dashboard">
      <div className="dashboard-header">
        <h1>Administración de Eventos</h1>
        <p>Agregar, editar o eliminar eventos para Espacio Paradigmas</p>

        {/* Events Section Toggle */}
        <div className="section-toggle">
          <div className="toggle-label">
            <span className="toggle-title">
              Visibilidad de Eventos en el Sitio
            </span>
            <span className="toggle-status">
              {siteSettings.showEvents ? "Activada" : "Desactivada"}
            </span>
          </div>
          <button
            onClick={toggleEventsVisibility}
            disabled={savingSettings}
            className={`visibility-toggle ${siteSettings.showEvents ? "active" : "inactive"}`}
          >
            {savingSettings ? (
              <span className="toggle-spinner"></span>
            ) : siteSettings.showEvents ? (
              <>
                <span className="toggle-icon">👁️</span>
                <span className="toggle-text">Ocultar Sección</span>
              </>
            ) : (
              <>
                <span className="toggle-icon">👁️‍🗨️</span>
                <span className="toggle-text">Mostrar Sección</span>
              </>
            )}
          </button>
          <p className="toggle-description">
            {siteSettings.showEvents
              ? "Los eventos son visibles en la agenda, página de inicio y navegación."
              : "Los eventos están ocultos del sitio público (solo visibles en este panel)."}
          </p>
        </div>
      </div>

      <div className="dashboard-content">
        {/* Event Form */}
        <div className="event-form-section">
          <h2>{editingEvent ? "Editar Evento" : "Agregar Nuevo Evento"}</h2>

          <form onSubmit={handleSubmit} className="event-form">
            <div className="form-group">
              <label>Título *</label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                placeholder="Título del evento"
                required
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Fecha *</label>
                <input
                  type="date"
                  name="date"
                  value={formData.date}
                  onChange={handleInputChange}
                  required
                />
              </div>

              <div className="form-group">
                <label>Hora *</label>
                <input
                  type="text"
                  name="time"
                  value={formData.time}
                  onChange={handleInputChange}
                  placeholder="ej. 19:00 o 7:00 PM"
                  required
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Duración</label>
                <input
                  type="text"
                  name="duration"
                  value={formData.duration}
                  onChange={handleInputChange}
                  placeholder="ej. 2 horas, 1.5 horas"
                />
              </div>

              <div className="form-group">
                <label>Costo</label>
                <input
                  type="text"
                  name="cost"
                  value={formData.cost}
                  onChange={handleInputChange}
                  placeholder="ej. $500, Gratis, A consultar"
                />
              </div>
            </div>

            <div className="form-group">
              <label>Enlace de Imagen</label>
              <input
                type="url"
                name="imgLink"
                value={formData.imgLink}
                onChange={handleInputChange}
                placeholder="ej. https://ejemplo.com/imagen.jpg"
              />
              <small className="field-hint">
                URL de imagen para mostrar en la agenda
              </small>
            </div>

            <div className="form-group">
              <label>Sitio Web</label>
              <input
                type="url"
                name="website"
                value={formData.website}
                onChange={handleInputChange}
                placeholder="ej. https://example.com"
              />
              <small className="field-hint">URL completa con https://</small>
            </div>

            <div className="form-group">
              <label>Descripción</label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                placeholder="Descripción del evento"
                rows="4"
              />
            </div>

            <div className="form-group checkbox-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  name="highlighted"
                  checked={formData.highlighted}
                  onChange={handleInputChange}
                  className="checkbox-input"
                />
                <span className="checkbox-custom"></span>
                <span className="checkbox-text">Evento Destacado</span>
              </label>
              <small className="field-hint">
                Marcar este evento como destacado (aparecerá de forma especial
                en la agenda)
              </small>
            </div>

            <div className="form-actions">
              <button type="submit" className="submit-btn">
                {editingEvent ? "Actualizar Evento" : "Agregar Evento"}
              </button>
              {editingEvent && (
                <button
                  type="button"
                  onClick={handleCancel}
                  className="cancel-btn"
                >
                  Cancelar
                </button>
              )}
            </div>
          </form>
        </div>

        {/* Events List */}
        <div className="events-list-section">
          <h2>Todos los Eventos ({events.length})</h2>

          {loading ? (
            <div className="loading">Cargando eventos...</div>
          ) : events.length === 0 ? (
            <div className="no-events">
              No se encontraron eventos. Agrega tu primer evento arriba.
            </div>
          ) : (
            <div className="events-table">
              <div className="table-header">
                <div className="table-cell">Fecha</div>
                <div className="table-cell">Título</div>
                <div className="table-cell">Destacado</div>
                <div className="table-cell">Hora</div>
                <div className="table-cell">Web</div>
                <div className="table-cell">Imagen</div>
                <div className="table-cell actions">Acciones</div>
              </div>

              {events.map((event) => (
                <div key={event.id} className="table-row">
                  <div className="table-cell date">
                    {formatDate(event.date)}
                  </div>
                  <div className="table-cell title">{event.title}</div>
                  <div className="table-cell highlighted">
                    <button
                      onClick={() =>
                        toggleHighlight(event.id, event.highlighted || false)
                      }
                      className={`highlight-btn ${event.highlighted ? "active" : ""}`}
                      title={
                        event.highlighted
                          ? "Quitar destacado"
                          : "Marcar como destacado"
                      }
                    >
                      {event.highlighted ? "★" : "☆"}
                    </button>
                  </div>
                  <div className="table-cell time">{event.time}</div>
                  <div className="table-cell website">
                    {event.website ? (
                      <a
                        href={event.website}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="website-link"
                        onClick={(e) => e.stopPropagation()}
                      >
                        🔗
                      </a>
                    ) : (
                      "-"
                    )}
                  </div>
                  <div className="table-cell image">
                    {event.imgLink ? (
                      <span className="image-link" title="Tiene imagen">
                        📷
                      </span>
                    ) : (
                      "-"
                    )}
                  </div>
                  <div className="table-cell actions">
                    <button
                      onClick={() => handleEdit(event)}
                      className="edit-btn"
                    >
                      Editar
                    </button>
                    <button
                      onClick={() => handleDelete(event.id)}
                      className="delete-btn"
                    >
                      Eliminar
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default AdminDashboard;
